import { useState, useEffect } from 'react';
import { getStreakData } from '../api';
import { useAuth } from '../contexts/AuthContext';
import GlassCard from './ui/GlassCard';

const StreakWidget = () => {
    const { session } = useAuth();
    const [streakData, setStreakData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (session) {
            fetchStreakData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session]);

    const fetchStreakData = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = session?.access_token;
            if (!token) {
                setError('Not authenticated');
                setLoading(false);
                return;
            }

            const data = await getStreakData(token);
            setStreakData(data);
        } catch (err) {
            console.error('Error fetching streak data:', err);
            setError('Failed to load streak');
        } finally {
            setLoading(false);
        }
    };

    const dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

    const styles = {
        container: {
            display: 'flex',
            gap: 'var(--space-xl)',
            alignItems: 'center',
            padding: 'var(--space-xl)',
        },
        iconContainer: {
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(239, 68, 68, 0.2))',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        fireEmoji: {
            fontSize: '2.5rem',
            animation: 'fire-pulse 2s ease-in-out infinite',
        },
        content: {
            flex: 1,
        },
        numberRow: {
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-sm)',
        },
        streakNumber: {
            fontFamily: 'var(--font-display)',
            fontSize: '3rem',
            fontWeight: 700,
            background: 'var(--gradient-streak)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: 1,
        },
        streakLabel: {
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'var(--amber-400)',
        },
        message: {
            color: 'var(--text-secondary)',
            fontSize: '0.9rem',
            marginBottom: 'var(--space-md)',
        },
        calendar: {
            display: 'flex',
            gap: 'var(--space-sm)',
        },
        calDay: {
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.75rem',
            fontWeight: 600,
        },
        calDayDefault: {
            background: 'var(--bg-surface)',
            color: 'var(--text-tertiary)',
        },
        calDayCompleted: {
            background: 'var(--amber-500)',
            color: 'white',
        },
        calDayCurrent: {
            background: 'transparent',
            border: '2px solid var(--amber-500)',
            color: 'var(--amber-400)',
        },
        skeleton: {
            padding: 'var(--space-xl)',
        },
        skeletonLine: {
            height: '16px',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-md)',
        },
    };

    if (loading) {
        return (
            <GlassCard variant="streak" style={styles.skeleton}>
                <div style={{ ...styles.skeletonLine, width: '50%' }} />
                <div style={{ ...styles.skeletonLine, width: '75%', height: '32px' }} />
                <div style={{ ...styles.skeletonLine, width: '60%' }} />
            </GlassCard>
        );
    }

    if (error) {
        return (
            <GlassCard variant="streak" style={{ padding: 'var(--space-xl)' }}>
                <p style={{ color: 'var(--red-500)' }}>Failed to load streak data</p>
            </GlassCard>
        );
    }

    if (!streakData) return null;

    const { current_streak, last_7_days } = streakData;

    // Determine which day is "today" (last in the array is today)
    const todayIndex = last_7_days.length - 1;

    // Get motivational message based on streak
    const getMessage = () => {
        if (current_streak >= 7) return "You're on fire! Incredible consistency.";
        if (current_streak >= 3) return "You're on fire! Keep the momentum going.";
        if (current_streak >= 1) return "Great start! Build that streak.";
        return "Start your streak today!";
    };

    return (
        <GlassCard variant="streak" style={styles.container}>
            {/* Fire Icon */}
            <div style={styles.iconContainer}>
                <span style={styles.fireEmoji}>🔥</span>
            </div>

            {/* Content */}
            <div style={styles.content}>
                <div style={styles.numberRow}>
                    <span style={styles.streakNumber}>{current_streak}</span>
                    <span style={styles.streakLabel}>Day Streak!</span>
                </div>

                <p style={styles.message}>{getMessage()}</p>

                {/* Week Calendar */}
                <div style={styles.calendar}>
                    {last_7_days.map((day, idx) => {
                        const isToday = idx === todayIndex;
                        const isCompleted = day.goal_met;

                        let dayStyle = { ...styles.calDay, ...styles.calDayDefault };
                        if (isCompleted && !isToday) {
                            dayStyle = { ...styles.calDay, ...styles.calDayCompleted };
                        } else if (isToday) {
                            dayStyle = { ...styles.calDay, ...styles.calDayCurrent };
                        }

                        return (
                            <div key={idx} style={dayStyle}>
                                {dayLabels[idx]}
                            </div>
                        );
                    })}
                </div>
            </div>
        </GlassCard>
    );
};

export default StreakWidget;
